import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowLeft } from "lucide-react";
import blogPosts from "@/data/blogPosts";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.25, 0.46, 0.45, 0.94] },
  }),
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="min-h-screen bg-white">
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm z-50 border-b border-gray-100">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-3 h-3 bg-black rounded-full" />
            <span className="text-lg font-semibold tracking-tight">
              <span className="text-gray-800">SHADOW</span>
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                LABS
              </span>
            </span>
          </Link>
          <Link
            to="/blog"
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={14} />
            All posts
          </Link>
        </nav>
      </header>

      <main className="pt-28 pb-24 px-4 sm:px-8">
        <div className="max-w-2xl mx-auto">
          {/* Post header */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            className="mb-12"
          >
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className="text-xs text-gray-400">{post.date}</span>
              <span className="w-1 h-1 rounded-full bg-gray-300" />
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <Clock size={11} />
                {post.readTime}
              </span>
              <span className="w-1 h-1 rounded-full bg-gray-300" />
              <span className="text-xs font-medium text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
                {post.persona}
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-gray-900 leading-tight">
              {post.title}
            </h1>
          </motion.div>

          {/* Post body */}
          <div className="space-y-5">
            {post.sections.map((section, i) => {
              if (section.type === "quote") {
                return (
                  <motion.blockquote
                    key={i}
                    custom={i + 1}
                    initial="hidden"
                    animate="visible"
                    variants={fadeUp}
                    className="border-l-4 border-teal-500 pl-5 py-1 my-8"
                  >
                    <p className="text-lg sm:text-xl font-medium text-gray-700 leading-relaxed italic">
                      "{section.content}"
                    </p>
                  </motion.blockquote>
                );
              }

              if (section.type === "heading") {
                return (
                  <motion.h2
                    key={i}
                    custom={i + 1}
                    initial="hidden"
                    animate="visible"
                    variants={fadeUp}
                    className="text-xl sm:text-2xl font-bold text-gray-900 mt-10 mb-2"
                  >
                    {section.content}
                  </motion.h2>
                );
              }

              if (section.type === "bullets") {
                return (
                  <motion.ul
                    key={i}
                    custom={i + 1}
                    initial="hidden"
                    animate="visible"
                    variants={fadeUp}
                    className="space-y-3 my-1"
                  >
                    {section.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-3">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-teal-500 flex-shrink-0" />
                        <span className="text-gray-600 text-base sm:text-lg leading-relaxed">
                          {item}
                        </span>
                      </li>
                    ))}
                  </motion.ul>
                );
              }

              return (
                <motion.p
                  key={i}
                  custom={i + 1}
                  initial="hidden"
                  animate="visible"
                  variants={fadeUp}
                  className="text-gray-600 text-base sm:text-lg leading-relaxed"
                >
                  {section.content}
                </motion.p>
              );
            })}
          </div>

          {/* CTA */}
          {post.cta && (
            <motion.div
              custom={post.sections.length + 2}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="mt-14 bg-gray-950 rounded-2xl px-8 py-8 text-center"
            >
              <p className="text-white text-base sm:text-lg leading-relaxed mb-5">
                {post.cta}
              </p>
              <a
                href="https://shadowlabs.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-gray-900 text-sm font-semibold px-6 py-3 rounded-xl hover:bg-gray-100 transition-colors duration-200"
              >
                Join the waitlist
              </a>
            </motion.div>
          )}

          {/* Keywords */}
          {post.keywords && (
            <motion.div
              custom={post.sections.length + 3}
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="mt-10 pt-8 border-t border-gray-100"
            >
              <div className="flex flex-wrap gap-2">
                {post.keywords.map((kw) => (
                  <span
                    key={kw}
                    className="text-xs text-gray-400 bg-gray-50 border border-gray-100 px-3 py-1 rounded-full"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </main>

      <footer className="border-t border-gray-100 py-10 px-4 sm:px-8">
        <div className="max-w-2xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="w-2.5 h-2.5 bg-black rounded-full" />
            <span className="text-sm font-semibold tracking-tight">
              <span className="text-gray-800">SHADOW</span>
              <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">
                LABS
              </span>
            </span>
          </Link>
          <Link
            to="/blog"
            className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
          >
            ← Back to blog
          </Link>
        </div>
      </footer>
    </div>
  );
}
